"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { usePathname } from "next/navigation";


type NavItem = { label: string; href: string };
const nav: NavItem[] = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Patient Success", href: "#testimonials" },
  { label: "FAQs", href: "#faqs" },
  { label: "Customer Support", href: "/support" },
];

const guideItems: NavItem[] = [
  { label: "What are GLP-1s?", href: "/guide/what-are-glp1" },
  { label: "Costs & Insurance", href: "/guide/costs" },
  { label: "Safety & Side Effects", href: "/guide/safety" },
];

// tweak for your sticky header height
const HEADER_OFFSET = 90;

function smoothScrollToHash(
  e: ReactMouseEvent<HTMLAnchorElement>,
  href: string,
  onDone?: () => void
) {
  if (!href.startsWith("#")) return; // only handle in-page links
  e.preventDefault();

  const id = href.slice(1);
  const el = document.getElementById(id);
  if (!el) return;

  const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;

  window.scrollTo({
    top: y,
    behavior: "smooth",
  });

  if (onDone) onDone();
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);


  // NEW: are we on the homepage?
  const pathname = usePathname();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={
        "sticky top-0 z-50 p-2 transition-colors"}
      role="banner"
    >
      <div
        className={[
          "mx-auto max-w-8xl px-8 py-2 rounded-[100px] bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75",
          "transition-all",
          scrolled ? "shadow-none" : "shadow-none",
        ].join(" ")}
      >
        <div className="flex h-16 items-center justify-between">
          {/* Left: Brand */}
          <Link
            href="/"
            aria-label="FeelGood home"
            className="flex items-center gap-2"
          >
            {/* Replace with your SVG/PNG if you have one */}
            {/* <img src="/logo-dark.svg" width="150" /> */} 
            
            <img src="/logo-dark.svg" className="w-[100px] md:w-[120px]" />
          </Link>
          <div className="flex flex-row font-semibold">
            {/* Center: Nav */}
            <nav className="hidden xl:flex items-center gap-8 text-[15px] text-slate-900">
              {nav.slice(0, 3).map((item) => {
                const isHash = item.href.startsWith("#");
                // NEW: if not on home, send them to "/#section"
                const targetHref =
                  isHash && !onHome ? `/${item.href}` : item.href;

                return (
                  <Link
                  key={item.href}
                  href={targetHref}
                  // only intercept & smooth-scroll WHEN we are already on "/"
                  onClick={
                    isHash && onHome
                      ? (e) => smoothScrollToHash(e, item.href)
                      : undefined
                  }
                  className="hover:text-slate-900"
                >
                  {item.label}
                </Link>
                );
              })}

              {/* Dropdown: GLP-1 Buying Guide */}
              {/* <div
                className="relative"
                onMouseEnter={() => setGuideOpen(true)}
                onMouseLeave={() => setGuideOpen(false)}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-slate-900"
                  aria-haspopup="menu"
                  aria-expanded={guideOpen}
                  onClick={() => setGuideOpen((v) => !v)}
                >
                  GLP-1 Buying Guide
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <path d="M5.5 7.5 10 12l4.5-4.5" />
                  </svg>
                </button>

                {guideOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 mt-3 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                  >
                    {guideItems.map((g) => (
                      <Link
                        key={g.href}
                        href={g.href}
                        role="menuitem"
                        className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        {g.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div> */}

              <Link href="/support" className="hover:text-slate-900">
                Customer Support
              </Link>
            </nav>

            {/* Right: Auth + CTA */}
            <div className="hidden xl:flex items-center gap-6">
              <span
                className="h-6 w-px ml-4 bg-slate-800"
                aria-hidden="true"
              />
              <Link
                href="/login"
                className="inline-flex items-center gap-1 text-[15px] text-slate-800 hover:text-slate-900"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  aria-hidden="true"
                >
                  <circle cx="12" cy="8" r="3" />
                  <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                </svg>
                Login
              </Link>
              <Link
                href="/start"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-2.5 text-[15px] font-medium text-white"
              >
                Get Started
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>

            {/* Mobile: hamburger */}
            {/* NEW: pass onHome down so mobile behaves the same */}
            <MobileMenu onHome={onHome} />
          </div>
        </div>
      </div>
    </header>
  );
}

/* --- Mobile menu (simple, no headless libs needed) --- */
function MobileMenu({ onHome }: { onHome: boolean }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleHashClick = (href: string) =>
    (e: ReactMouseEvent<HTMLAnchorElement>) => {
      if (onHome) {
        // scroll on homepage
        smoothScrollToHash(e, href, () => setOpen(false));
      }
      // Else: do NOT prevent, Next.js will navigate to "/#hash"
    };



  // CLICK OUTSIDE TO CLOSE
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;

      if (menuRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;

      setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);


  return (
    <div className="xl:hidden">
      <button
        ref={buttonRef}
        aria-label="Toggle menu"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-slate-100"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-6 w-6"
          fill="none"
          stroke="#0f172b"
          strokeWidth={1.8}
        >
          <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute inset-x-0 top-[80px] z-50 border-white-100 rounded-xl bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80 px-4 pb-6 pt-4 shadow-xl"
        >
          <div className="flex flex-col gap-3 text-[15px] text-slate-800">
            <Link
              href={onHome ? "#how-it-works" : "/#how-it-works"}
              scroll={false}
              onClick={handleHashClick("#how-it-works")}
            >
              How It Works
            </Link>
            <Link
              href={onHome ? "#testimonials" : "/#testimonials"}
              scroll={false}
              onClick={handleHashClick("#testimonials")}
            >
              Patient Success
            </Link>
            <Link
              href={onHome ? "#faqs" : "/#faqs"}
              scroll={false}
              onClick={handleHashClick("#faqs")}
            >
              FAQs
            </Link>

            {/* (kept your commented-out code) */}

            <Link href="/support" onClick={() => setOpen(false)}>
              Customer Support
            </Link>
            <Link
              href="/login"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <circle cx="12" cy="8" r="3" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              </svg>
              Login
            </Link>
            <Link
              href="/start"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-2.5 text-[15px] font-medium text-white"
            >
              Get Started
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
